import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import gmfLogo from "@/assets/gmf-logo.png";

export default function Footer() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <footer ref={ref} className="bg-primary text-primary-foreground overflow-hidden">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Section */}
          <div 
            className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0ms' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={gmfLogo} 
                alt="GMF Custom Furniture" 
                className="h-16 w-auto transition-transform duration-300 hover:scale-105"
                width={64}
                height={64}
                loading="lazy"
              />
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold text-primary-foreground">Ghulam Mustafa Furniture</span>
                <span className="text-xs text-gold font-medium" style={{ fontFamily: 'Arial, sans-serif' }}>غلام مصطفى لتجارة الاثاث ش.ذ.م.م</span>
              </div>
            </div>
            <p className="text-gold font-semibold text-sm mb-2">30+ Years of Furniture Manufacturing Excellence</p>
            <p className="text-sm text-primary-foreground/80 mb-4">
              Crafting quality sofas, beds & wardrobes in Dubai, Abu Dhabi, Sharjah, and All Over the UAE. GMF is UAE's premier custom furniture manufacturer, specializing in bespoke furniture, blinds, curtains, and upholstery services for luxury homes and commercial spaces.
            </p>
          </div>

          {/* Quick Links */}
          <div 
            className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <h3 className="font-semibold text-gold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-gold transition-all duration-300 hover:translate-x-1 inline-block">Home</Link></li>
              <li><Link to="/customize-furniture/bed-headboard" className="hover:text-gold transition-all duration-300 hover:translate-x-1 inline-block">Custom Furniture</Link></li>
              <li><Link to="/blinds-curtains/blinds/roller" className="hover:text-gold transition-all duration-300 hover:translate-x-1 inline-block">Blinds & Curtains</Link></li>
              <li><Link to="/upholstery/indoor" className="hover:text-gold transition-all duration-300 hover:translate-x-1 inline-block">Upholstery Services</Link></li>
              <li><Link to="/projects" className="hover:text-gold transition-all duration-300 hover:translate-x-1 inline-block">Our Projects</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-all duration-300 hover:translate-x-1 inline-block">Contact Us</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div 
            className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <h3 className="font-semibold text-gold mb-4">Our Services</h3>
            <ul className="space-y-2 text-sm">
              <li className="transition-colors duration-300 hover:text-gold/80">Custom Beds & Headboards</li>
              <li className="transition-colors duration-300 hover:text-gold/80">Bespoke Sofas & Chairs</li>
              <li className="transition-colors duration-300 hover:text-gold/80">TV Units & Wall Design</li>
              <li className="transition-colors duration-300 hover:text-gold/80">Walk-in Wardrobes</li>
              <li className="transition-colors duration-300 hover:text-gold/80">Window Blinds Installation</li>
              <li className="transition-colors duration-300 hover:text-gold/80">Curtain Making & Fitting</li>
              <li className="transition-colors duration-300 hover:text-gold/80">Furniture Upholstery</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div 
            className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '300ms' }}
          >
            <h3 className="font-semibold text-gold mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3 group">
                <MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110" />
                <div className="space-y-1">
                  <span className="block">AL Quoz, Opposite of Emirates Mall, Shed #4, Area D - Dubai - UAE</span>
                  <span className="block">Industrial Area 04, Sharjah - UAE</span>
                </div>
              </li>
              <li className="flex items-center gap-3 group">
                <Phone className="h-5 w-5 text-gold shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
                <div className="space-y-1">
                  <a href="tel:+971552622889" className="block hover:text-gold transition-colors duration-300">+971 55 262 2889</a>
                  <a href="tel:+971588797000" className="block hover:text-gold transition-colors duration-300">+971 58 879 7000</a>
                </div>
              </li>
              <li className="flex items-center gap-3 group">
                <Mail className="h-5 w-5 text-gold shrink-0 transition-transform duration-300 group-hover:scale-110" />
                <a className="hover:text-gold transition-colors duration-300" href="mailto:info@gmfdubai.com">info@gmfdubai.com</a>
              </li>
              <li className="flex items-center gap-3 group">
                <Clock className="h-5 w-5 text-gold shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
                <span>Sat-Thu: 8AM-9PM | Fri: 8AM-11AM</span>
              </li>
            </ul>
          </div>
        </div>

        <div 
          className={`border-t border-primary-foreground/20 mt-8 pt-8 text-center text-sm text-primary-foreground/60 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '400ms' }}
        >
          <p>&copy; {new Date().getFullYear()} GMF Custom Furniture Dubai, Abu Dhabi, Sharjah, and All Over the UAE. All rights reserved.</p>
          <p className="mt-2">Premium Custom Furniture Manufacturer in Dubai, Abu Dhabi, Sharjah, and All Over the UAE</p>
        </div>
      </div>
    </footer>
  );
}
